import { isPlatformBrowser } from '@angular/common';
import { AfterContentInit, Component, Input, NgZone, OnInit, OnDestroy, ElementRef, Output, EventEmitter, PLATFORM_ID, Inject, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { closest } from 'src/app/util';

@Component({
  selector: 'tm-auto-complete-options',
  templateUrl: './auto-complete-options.component.html',
  styleUrls: ['./auto-complete-options.component.scss'],
  exportAs: 'tmAutoCompleteOptions'
})
export class AutoCompleteOptionsComponent implements OnInit, AfterContentInit, OnDestroy, OnChanges {
  /** 是否為瀏覽器端渲染 */
  isBrowser: boolean;
  @ViewChild('optionsList', {static: false}) set optionsListRef(ref: ElementRef) {
    if (ref) {
      this._optionsListEl = ref.nativeElement;
    }
  }
  /** 選像列表(ul)的索引 */
  private _optionsListEl: HTMLElement;
  /** 選像列表(ul)的索引，可在外頭用組件索引取得 ul 的索引做操作 */
  get optionsListEl() { return this._optionsListEl || null; }
  /** 選項 */
  @Input() options: string[] = [];
  /** 開關這個選像框的元素 */
  private _togglerEl: HTMLInputElement;
  /** 開關這個選像框的元素 */
  @Input() set togglerEl(el: HTMLInputElement) {
    if (this._togglerEl) {
      this._togglerEl.removeEventListener('keydown', this.onArrowKeyEventHandler);
      this._togglerEl.removeEventListener('compositionstart', this.onCompositionstartHanlder);
      this._togglerEl.removeEventListener('compositionend', this.onCompositionendHanlder);
      this._togglerEl.setAttribute('aria-description', '');
    }
    if (el) {
      this._togglerEl = el;
      this._togglerEl.addEventListener('keydown', this.onArrowKeyEventHandler);
      this._togglerEl.addEventListener('compositionstart', this.onCompositionstartHanlder);
      this._togglerEl.addEventListener('compositionend', this.onCompositionendHanlder);
      this._togglerEl.setAttribute('aria-description', '請使用鍵盤方向上下鍵走訪選項，點擊Enter鍵可選取選項，點擊Esc鍵關閉選單，使用方向鍵可再次展開選單');
    } else if (this._togglerEl) {
      delete this._togglerEl;
    }
  }
  /** 開關這個選像框的元素 */
  get togglerEl() { return this._togglerEl; }
  /** 當選取一個詞就拋出 */
  @Output() selected = new EventEmitter<string>();
  /** dom 元素 id，應該自行指定唯一值，預設為 'options' */
  @Input() id = 'options';
  /** 選像列標籤，應自行在組件外指定，預設為 '自動完成選項' */
  @Input() label = '自動完成選項';
  /** 是否顯示下拉選項 */
  isShowOptions = false;
  /** 目前走訪的選項名稱 */
  currentViewedOptionIndex = -1;
  /** 判斷搜尋輸入框是否已經輸入完成 */
  isCompositionend = true;

  constructor(
    @Inject(PLATFORM_ID) protected platformId: object,
    private el: ElementRef,
    private zone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && changes.options.currentValue && changes.options.currentValue.length) {
      // this.toFirstOption();
    }
  }

  ngAfterContentInit(): void {
    if (this.isBrowser) {
      document.body.addEventListener('focusin', this.onFocusInEventHandler);
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      document.body.removeEventListener('focusin', this.onFocusInEventHandler);
      if (this._togglerEl) {
        this._togglerEl.removeEventListener('keydown', this.onArrowKeyEventHandler);
        this._togglerEl.removeEventListener('compositionstart', this.onCompositionstartHanlder);
        this._togglerEl.removeEventListener('compositionend', this.onCompositionendHanlder);
        this._togglerEl.setAttribute('aria-description', '');
      }
    }
  }

  /** 監聽方向鍵盤點擊事件，用上下鍵走訪選項 */
  onArrowKeyEventHandler = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
      if (this.isShowOptions) {
        // 往上一個選項
        if (--this.currentViewedOptionIndex < 0) {
          this.toLastOption();
        } else {
          this.toPrevOption();
        }
      } else {
        // 打開選單
        this.isShowOptions = true;
      }
    } else if (event.key === 'ArrowDown') {
      if (this.isShowOptions) {
        // 往下一個選項
        if (++this.currentViewedOptionIndex >= this.options.length) {
          this.toFirstOption();
        } else {
          this.toNextOption();
        }
      } else {
        // 打開選單
        this.isShowOptions = true;
      }
    } else if (event.key === 'Escape') {
      // 關閉選單
      this.closeOptionsList();
    } else if (event.key === 'Enter') {
      if (!this.isShowOptions || this.currentViewedOptionIndex < 0) {
        // 直接送關鍵字
        this.selected.emit(this._togglerEl.value);
      } else if (this.options && this.options.length && this.isCompositionend) {
        // 選取選項
        this.onClickOption(this.options[this.currentViewedOptionIndex]);
      }
    }
  }

  /** 全局監聽焦點事件 */
  onFocusInEventHandler = (event: Event) => {
    const tg = event.target as HTMLElement;
    const toggler = this.togglerEl;
    const blurContainer = this.el.nativeElement as HTMLElement;

    // 焦點在開關上
    if (toggler === tg) { return; }

    // 焦點在容器內
    let find = closest(tg, blurContainer);
    if (find) { return; }

    // 焦點在開關和容器以外
    this.zone.run(() => {
      if (this.isShowOptions) {
        this.closeOptionsList();
      }
    });
  }

  /** 針對中文輸入組字判斷目前是否已經拼字完成 */
  onCompositionstartHanlder = (event: CompositionEvent) => {
    this.isCompositionend = false;
  }

  /** 針對中文輸入組字判斷目前是否已經拼字完成 */
  onCompositionendHanlder = (event: CompositionEvent) => {
    this.isCompositionend = true;
  }

  /** 瀏覽第一個選項 */
  toFirstOption() {
    this.currentViewedOptionIndex = 0;
    const scrollContainer = (this.el.nativeElement as HTMLElement).querySelector('.key-options-wrapper') as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }

  /** 瀏覽最後一個選項 */
  toLastOption() {
    this.currentViewedOptionIndex = this.options.length - 1;
    const scrollContainer = (this.el.nativeElement as HTMLElement).querySelector('.key-options-wrapper') as HTMLElement;
    scrollContainer.scrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;
  }

  /** 瀏覽下一個兄弟選項 */
  toNextOption() {
    this.scrollToOption(this.currentViewedOptionIndex, 1);
  }

  /** 瀏覽上一個兄弟選項 */
  toPrevOption() {
    this.scrollToOption(this.currentViewedOptionIndex, -1);
  }

  /** 卷軸滾動到某個兄弟選項的位置 */
  scrollToOption(index: number, diretion: number) {
    const optEl = (this.el.nativeElement as HTMLElement).querySelector(`a[role="option"][data-index="${index}"]`) as HTMLElement;
    const scrollContainer = (this.el.nativeElement as HTMLElement).querySelector('.key-options-wrapper') as HTMLElement;
    const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;

    if (diretion > 0 && optEl.offsetTop + optEl.offsetHeight < scrollContainer.offsetHeight) { // 在第一頁內向下
      scrollContainer.scrollTop = 0;
    } else if (diretion < 0 && optEl.offsetTop >= maxScrollTop) { // 在最後一頁內向上
      scrollContainer.scrollTop = maxScrollTop;
    } else if (scrollContainer.scrollTop <= maxScrollTop) {
      let newTop = scrollContainer.scrollTop + (optEl.offsetHeight * diretion);
      if (newTop > maxScrollTop) { // 再最後一頁最多只能滾動到最大值
        newTop = maxScrollTop;
      }
      scrollContainer.scrollTop = newTop;
    }
  }

  /** 關閉下拉選單區塊 */
  closeOptionsList() {
    this.isShowOptions = false;
    this.currentViewedOptionIndex = -1;
    // if (this.options && this.options.length) {
    //   this.toFirstOption();
    // }
  }

  /** 當點選了某個選項 */
  onClickOption(option: string) {
    this.selected.emit(option);
    this.closeOptionsList();
  }
}
